/**
 * Where an answer belongs, as the answers list and the drawer say it: "on
 * case INV-… title", "on item QT-… title", or "not attached to a case ·
 * Attach to a case ▾" with a picker of the open cases (proposal 1).
 */
import { Link } from 'react-router-dom';

import { isActiveCase } from '../portal/investigationsView';
import type { DoorInvestigationRead } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import type { AnswerRow } from '../../pages/quality-triage/answersView';
import { fromTab } from '../../pages/quality-triage/qualityWorkspaceView';
import styles from './AnswersForYou.module.css';

interface AnswerPlaceProps {
  row: AnswerRow;
  investigations: DoorInvestigationRead[];
  busy: boolean;
  onAttach: (requestId: string, investigationId: string) => void;
  /** Also offer Attach on an answer that sits on an item but has no case
   * (the drawer, where Add to case findings needs a case). */
  attachOnItem?: boolean;
}

/** The "Attach to a case ▾" picker over the open cases; null when none is open. */
function AttachPicker({ row, investigations, busy, onAttach }: Omit<AnswerPlaceProps, 'attachOnItem'>) {
  const open = investigations.filter(isActiveCase);
  if (open.length === 0) return null;
  return (
    <>
      {' · '}
      <select
        className={styles.attach}
        aria-label={`Attach ${row.who}'s answer to a case`}
        value=""
        disabled={busy}
        onChange={(e) => {
          const investigationId = e.target.value;
          if (!investigationId) return;
          const inv = open.find((i) => i.id === investigationId);
          trackEvent('action', 'answers_attach', {
            data_request_id: row.request.id,
            investigation_id: investigationId,
            investigation_no: inv?.investigation_no ?? null,
            thread_no: row.thread?.thread_no ?? null,
            result: 'request_sent',
          });
          onAttach(row.request.id, investigationId);
        }}
      >
        <option value="">Attach to a case ▾</option>
        {open.map((inv) => (
          <option key={inv.id} value={inv.id}>
            {inv.investigation_no} · {inv.title}
          </option>
        ))}
      </select>
    </>
  );
}

/** Where the answer belongs: its case, else its item, else Attach. */
export function AnswerPlace({ row, investigations, busy, onAttach, attachOnItem = false }: AnswerPlaceProps) {
  if (row.investigation && row.path) {
    return (
      <>
        on case{' '}
        <Link to={row.path} state={fromTab('cases').state} className={styles.place}>
          {row.investigation.investigation_no} {row.investigation.title}
        </Link>
      </>
    );
  }
  if (row.thread && row.path) {
    return (
      <>
        on item{' '}
        <Link to={row.path} state={fromTab('triage').state} className={styles.place}>
          {row.thread.thread_no} {row.thread.title}
        </Link>
        {attachOnItem && <AttachPicker row={row} investigations={investigations} busy={busy} onAttach={onAttach} />}
      </>
    );
  }
  return (
    <>
      <span className={styles.loose}>not attached to a case</span>
      <AttachPicker row={row} investigations={investigations} busy={busy} onAttach={onAttach} />
    </>
  );
}
